import React, { useState } from "react";
import styled from 'styled-components';
import { MedicationItem, AdministrationDetails, Drug, Drip } from "./types";
import { MedicationGivenInfo } from "./CprState/types";
import MedicationTableRow from "./MedicationTableRow";
import MedicationConfirmationDialog from "./MedicationDialogs/MedicationConfirmationDialog";
import DripConfirmationDialog from "./MedicationDialogs/DripConfirmationDialog";
import DripInfusionSpeedConfirmationDialog from "./MedicationDialogs/DripInfusionSpeedConfirmationDialog";
import { getMedicationDetails } from "../Resus/predicateUtils";
import { useResusContext } from "../Resus/ResusContext";
import { FaTrashCan } from "react-icons/fa6";
import { useTranslation } from 'react-i18next';

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #103C6E;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`;

interface MedicationTableProps {
	title?: string;
	items: MedicationItem[];
	lastGivenInfo: { [key: string]: MedicationGivenInfo };
	onGiveMedication: (item: MedicationItem, administrationDetails: AdministrationDetails) => void;
	drugComments?: { [key: string]: string | undefined};
	showRemoveButton?: boolean;
	onRemove?: () => void;
}

const MedicationTable: React.FC<MedicationTableProps> = ({
	title,
	items,
	lastGivenInfo,
	onGiveMedication,
	drugComments = {},
	showRemoveButton = false,
	onRemove
}) => {
	const { t } = useTranslation(['resus']);
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [selectedItem, setSelectedItem] = useState<MedicationItem | null>(null);
	const { weight, age } = useResusContext();  
	const sortedItems = [...items].sort((a, b) => {
		const isAEndotracheal = !a.isDrip && a.howToGive?.toLowerCase().includes("endotracheal");
		const isBEndotracheal = !b.isDrip && b.howToGive?.toLowerCase().includes("endotracheal");
		if (isAEndotracheal && !isBEndotracheal) return 1;
		if (!isAEndotracheal && isBEndotracheal) return -1;
		return 0;
	});

	const handleGiveMedication = (item: MedicationItem) => {
		setSelectedItem(item);
		setShowConfirmModal(true);
	};

	const handleConfirm = (dosage?: AdministrationDetails) => {
		if (selectedItem && dosage) {
			onGiveMedication(selectedItem, dosage);
		}
		setShowConfirmModal(false);
		setSelectedItem(null);
	};

	const renderConfirmationDialog = () => {
		if (!selectedItem || !showConfirmModal) return null;
		const selectedItemDetails = getMedicationDetails(selectedItem, age || 0, weight || 0, t);
		if (selectedItemDetails.isDrip) {
			if (selectedItemDetails.calc_type === "InfusionSpeed") {
				return (
					<DripInfusionSpeedConfirmationDialog
						isOpen={showConfirmModal}
						setIsOpen={setShowConfirmModal}
						drip={selectedItemDetails as Drip}
						onConfirm={handleConfirm}
					/>
				);
			}
			return (
				<DripConfirmationDialog
					isOpen={showConfirmModal}
					setIsOpen={setShowConfirmModal}
					drip={selectedItemDetails as Drip}
					onConfirm={handleConfirm}
				/>
			);
		}

		return (
			<MedicationConfirmationDialog
				isOpen={showConfirmModal}
				setIsOpen={setShowConfirmModal}
				drug={selectedItemDetails as Drug}
				onConfirm={handleConfirm}
			/>
		);
	};

	return (
		<>
			<div
				style={{
					width: "100%",
					direction: "rtl",
				}}
				className="Medication"
			>
			{title && (
			<div
				style={{
				textAlign: "right",
				padding: "12px",
				fontWeight: "600",
				fontSize: "1.2rem",
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center"
				}}
			>
				<span>{title}</span>
				{showRemoveButton && onRemove && (
				<RemoveButton 
				onClick={onRemove}
				>
					<FaTrashCan />
				</RemoveButton>
				)}
			</div>
			)}
				<div className="Cards">
					{items.length === 0 ? (
						<div
							style={{
								padding: "12px",
								textAlign: "right",
							}}
						>
							{t('medicationTable.noDrugsFound')}
						</div>
					) : (
						sortedItems.map((item) => (
							<MedicationTableRow
								key={item.id}
								item={item}
								givenInfo={lastGivenInfo[item.id]}
								onGiveMedication={handleGiveMedication}
								customComment={drugComments[item.id]}
							/>
						))
					)}
				</div>
			</div>

			{renderConfirmationDialog()}
		</>
	);
};

export default MedicationTable;
